import { type LoyaltyUserRegistrationInfo } from "@koala/sdk";
import Router from "next/router";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { SignUpForm, type SignUpFormFieldValues } from "./form";
import ReCaptcha, {
  type ReCaptchaRef,
} from "@/components/uielements/recaptcha";
import { CAPTCHA_CONFIG } from "@/constants/authentication";
import { DATE_FORMAT } from "@/constants/dates";
import { useSelector } from "@/redux";
import authActions from "@/redux/auth/actions";
import { sleep } from "@/utils";
import { getExtraSignupPayload } from "@/utils/auth";
import { formatDate, getDate, isValidDate } from "@/utils/dates";
import * as ErrorReporter from "@/utils/errorReporter";
import { parseReferrer } from "@/utils/global";

export function SignUpFormContainer() {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.app.auth.loading);
  const loyaltyDriverID = useSelector(
    (state) => state.app.organization.organization.loyalty_driver_id
  );
  const [recaptchaRef, setRecaptchaRef] = useState<ReCaptchaRef | undefined>(
    undefined
  );

  const signUpFactory = (
    formValues: SignUpFormFieldValues,
    captchaResponse?: string | null
  ) => {
    // Respect the referrer ONLY if we're on the login page
    const referrer = parseReferrer(Router);
    const payload: LoyaltyUserRegistrationInfo = {
      ...formValues,
      ...getExtraSignupPayload(loyaltyDriverID),
      captcha_response: captchaResponse ?? undefined,
    };

    dispatch(authActions.signUp(payload, referrer));

    // If we're using a captcha, reset the captcha
    if (captchaResponse) {
      recaptchaRef?.current?.reset();
    }
  };

  const checkReCaptcha = async (formValues: SignUpFormFieldValues) => {
    const normalizedFormValues = Object.assign({}, formValues);

    if (normalizedFormValues.birthday) {
      if (
        isValidDate(
          /** @TODO ensure that `birthday` is defined. */
          // @ts-expect-error
          getDate(formValues.birthday, DATE_FORMAT.MONTH_DAY_YEAR_DASHED)
        )
      ) {
        normalizedFormValues.birthday = formatDate(
          /** @TODO ensure that `birthday` is defined. */
          // @ts-expect-error
          getDate(formValues.birthday, DATE_FORMAT.MONTH_DAY_YEAR_DASHED),
          DATE_FORMAT.YEAR_MONTH_DAY_DASHED
        );
      } else {
        normalizedFormValues.birthday = formatDate(
          /** @TODO ensure that `birthday` is defined. */
          // @ts-expect-error
          getDate(formValues.birthday, DATE_FORMAT.MONTH_DAY_YEAR),
          DATE_FORMAT.YEAR_MONTH_DAY_DASHED
        );
      }
    }

    if (recaptchaRef?.current) {
      let captchaResponse = null;
      try {
        // if after 5 seconds reCAPTCHA has not resolved
        // continue through the registration flow and let the API errors propagate
        captchaResponse = await Promise.race([
          recaptchaRef.current.executeAsync(),
          sleep(5000),
        ]);
      } catch (error) {
        console.error(error);
        ErrorReporter.captureException(error);
      } finally {
        signUpFactory(normalizedFormValues, captchaResponse);
      }
    } else {
      signUpFactory(normalizedFormValues);
    }
  };

  return (
    <>
      <SignUpForm loading={loading} doSignUp={checkReCaptcha} />

      {/* Conditional ReCaptcha */}
      <ReCaptcha
        onRef={setRecaptchaRef}
        forwardRef={recaptchaRef}
        // Captcha is not supported for sign up!
        // we mount ReCaptcha component anyway because captcha
        // response is forwarded through to the login flow
        action={CAPTCHA_CONFIG.API_ACTIONS.LOGIN}
      />
    </>
  );
}
